// composables/useConfirmDialog.ts
import { ref, watch, markRaw } from 'vue';
import type { Component } from 'vue';
import { appT } from '~/composables/useAppI18n';
import { useUiToast } from './useUiToast';

// 전역 상태 (모든 컴포넌트에서 공유)
const globalConfirm = ref({
  visible: false,
  type: '',
  title: '',
  message: '' as string | Component,
  showCancelButton: false,
  autoClose: true,
  formData: {} as Record<string, any>,
  confirmButtonText: '', // 확인 버튼 문구
  cancelButtonText: '', // 취소 버튼 문구
  /** true이면 Enter/Space 키로 버튼이 눌리지 않음 (마우스 클릭만 허용) */
  mouseOnly: false,
  /** true이면 확인(예) 버튼을 취소(아니오) 버튼 왼쪽에 배치 */
  confirmButtonFirst: false,
  /** 푸터 왼쪽(버튼 앞) 보조 안내 문구 */
  footerHint: '',
});

// visible 상태 변경 추적용 워처 (디버깅용)
watch(
  () => globalConfirm.value.visible,
  (newVal, oldVal) => {
    // eslint-disable-next-line no-console
    console.log('[GlobalConfirm] visible changed:', oldVal, '->', newVal, {
      type: globalConfirm.value.type,
      title: globalConfirm.value.title,
      message: globalConfirm.value.message,
    });
  },
);

// 전역 콜백 함수 저장 (formData를 인자로 받을 수 있도록 수정)
const globalConfirmCallback = ref<((formData?: Record<string, any>) => void) | null>(null);
const globalCancelCallback = ref<(() => void) | null>(null);

// 자동 닫기 타이머
let autoCloseTimer: NodeJS.Timeout | null = null;

export type ConfirmDialogOptions = {
  mouseOnly?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonFirst?: boolean;
};

const resetConfirmState = () => {
  globalConfirm.value.visible = false;
  globalConfirm.value.message = '';
  globalConfirm.value.formData = {};
  globalConfirm.value.confirmButtonText = '';
  globalConfirm.value.cancelButtonText = '';
  globalConfirm.value.footerHint = '';
  globalConfirm.value.mouseOnly = false;
  globalConfirm.value.confirmButtonFirst = false;
  globalConfirmCallback.value = null;
  globalCancelCallback.value = null;
};

const handleConfirm = () => {
  // eslint-disable-next-line no-console
  console.log('[GlobalConfirm] handleConfirm called');
  const cb = globalConfirmCallback.value;
  const formData = { ...globalConfirm.value.formData }; // 복사본 생성

  // 자동 닫기 타이머가 있으면 취소
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  // 먼저 닫아주고
  resetConfirmState();

  // 다음 틱에 콜백 실행(중첩 confirm 지원)
  setTimeout(() => {
    if (cb) cb(formData);
  }, 0);
};

const handleCancel = () => {
  // eslint-disable-next-line no-console
  console.log('[GlobalConfirm] handleCancel called');
  // 자동 닫기 타이머가 있으면 취소
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  // 취소 콜백이 있으면 실행
  if (globalCancelCallback.value) {
    globalCancelCallback.value();
  }
  resetConfirmState();
};

// 전역 알림창 표시 함수
export const showConfirm = (
  type: string,
  title: string,
  message: string | Component,
  showCancelButton: boolean = false,
  onConfirm: ((formData?: Record<string, any>) => void) | null = null,
  onCancel: (() => void) | null = null,
  autoClose: boolean = true,
  footerHint: string = '',
  options: ConfirmDialogOptions = {},
) => {
  // 기존 타이머가 있으면 취소
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  globalConfirm.value.type = type;
  globalConfirm.value.title = title;
  globalConfirm.value.message = typeof message === 'string' ? message : markRaw(message);
  globalConfirm.value.showCancelButton = showCancelButton;
  globalConfirm.value.autoClose = autoClose;
  globalConfirm.value.formData = {}; // 빈 객체로 초기화
  globalConfirm.value.footerHint = footerHint ?? '';
  globalConfirm.value.mouseOnly = options.mouseOnly ?? false;
  globalConfirm.value.confirmButtonFirst = options.confirmButtonFirst ?? false;

  // autoClose 적용 시 확인 버튼 문구를 바꿔줌
  if (options.confirmButtonText) {
    globalConfirm.value.confirmButtonText = options.confirmButtonText;
  } else if (autoClose && !showCancelButton) {
    globalConfirm.value.confirmButtonText = appT('common.confirmAutoClose');
  } else {
    globalConfirm.value.confirmButtonText = appT('common.confirm');
  }

  globalConfirm.value.cancelButtonText = options.cancelButtonText ?? appT('common.action.cancel');

  globalConfirm.value.visible = true;
  globalConfirmCallback.value = onConfirm;
  globalCancelCallback.value = onCancel;

  // autoClose가 true이고 취소 버튼이 없는 경우(alert)만 자동으로 닫기
  if (autoClose && !showCancelButton) {
    autoCloseTimer = setTimeout(() => {
      resetConfirmState();
      autoCloseTimer = null;
    }, 1250);
  }
};

// 전역 간단한 알림창 함수
/** 서버·레거시 메시지의 br 태그를 토스트(plain text) 줄바꿈으로 변환 */
const formatAlertMessageForToast = (message: string): string => {
  return message.replace(/<br\s*\/?>/gi, '\n');
};

export const showAlert = (
  title: string = '안내',
  message: string | Component,
  type: string = 'info',
  autoClose: boolean = true,
) => {
  // useUiToast로 래핑하여 사용
  const { showToast } = useUiToast();

  // Component 타입은 지원하지 않으므로 string으로 처리
  if (typeof message !== 'string') {
    console.warn('[showAlert] Component 타입 message는 toast로 표시할 수 없습니다.');
    return;
  }

  // type 매핑 (showConfirm의 type -> useUiToast의 type)
  const toastType: 'saving' | 'saved' | 'error' | 'success' | 'warning' | 'info' =
    type === 'success' ? 'saved' : (type as any);

  // duration 설정 (autoClose가 true면 4000ms, false면 0)
  const duration = autoClose ? 3000 : 0;

  showToast(toastType, formatAlertMessageForToast(message), duration);

  // 기존 로직 (주석 처리)
  // showConfirm(type, title, message, false, null, null, autoClose);
};

// 전역 확인/취소 알림창 함수
export const showConfirmDialog = (
  title: string,
  message: string | Component,
  onConfirm: (formData?: Record<string, any>) => void,
  onCancel?: () => void,
  type: string = 'warning',
  footerHint?: string,
  options?: ConfirmDialogOptions,
) => {
  showConfirm(type, title, message, true, onConfirm, onCancel ?? null, false, footerHint ?? '', options ?? {});
};

/** 특정 안내 문구의 확인창이 열려 있으면 닫기 (평가 화면 전환 시 잔류 팝업 방지) */
export const dismissConfirmIfMessage = (message: string | Component) => {
  if (!globalConfirm.value.visible) return false;
  const current = globalConfirm.value.message;
  const matches =
    current === message ||
    (typeof current === 'string' &&
      (current.includes('이전 평가자의 평가 결과를 조정하는 방식') ||
        current.includes('adjust the previous evaluator')));
  if (!matches) return false;
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }
  resetConfirmState();
  return true;
};

export const confirmAsync = (title: string, message: string | Component): Promise<boolean> => {
  return new Promise((resolve) => {
    showConfirmDialog(
      title,
      message,
      () => resolve(true),
      () => resolve(false),
    );
  });
};

/**
 * 삭제 등 로컬 반영 후 저장이 필요할 때 푸터에 표시하는 공통 안내 (신뢰 가능한 정적 HTML, UiConfirm v-html)
 */
export const SAVE_TO_APPLY_FOOTER_HINT =
  '* <span class="ui-confirm__footer-hint-accent">저장 버튼 클릭</span> 시 최종 반영됩니다.';

export const useConfirmDialog = () => {
  return {
    confirm: globalConfirm,
    handleConfirm,
    handleCancel,
    showConfirm,
    showAlert,
    showConfirmDialog,
    confirmAsync,
    dismissConfirmIfMessage,
  };
};
